<template>
  <div>
    <Header />

    <!-- Loading -->
    <div v-if="loading" class="state-block">
      <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
      <p class="mt-3 text-muted">Loading payrun...</p>
    </div>

    <template v-else>

      <!-- Back + breadcrumb -->
      <div class="back-row">
        <button class="btn-back" @click="$router.back()">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
      <div class="page-header">
        <div>
          <h5 class="page-title">Payrun Details</h5>
          <p class="page-sub">
            <router-link :to="$url('/payroll')" class="breadcrumb-link">Payroll</router-link>
            <span class="breadcrumb-sep">/</span>
            <span>Payrun {{ runDetails.reference }}</span>
          </p>
        </div>
      </div>

      <!-- Run header card -->
      <div class="run-header-card">
        <div class="run-header-left">
          <div class="run-header-icon"><font-awesome-icon icon="money-bill-wave" /></div>
          <div>
            <div class="run-header-title-row">
              <h2 class="run-period">{{ summary.period }}</h2>
              <span class="status-badge" :class="statusClass(summary.status)">{{ summary.status }}</span>
            </div>
            <p class="run-date">Run Date: {{ summary.runDate }}</p>
          </div>
        </div>

        <div class="run-header-actions">
          <!-- Posted -->
          <div v-if="summary.status === 'posted'" class="workflow-pill workflow-pill--purple">
            <font-awesome-icon icon="check-double" /> Posted to Accounts
          </div>
          <!-- Paid — show post button -->
          <template v-else-if="canInitiatePosting">
            <div class="workflow-pill workflow-pill--teal">
              <font-awesome-icon icon="money-bill-wave" /> Payment Initiated
            </div>
            <button class="btn-initiate btn-initiate--purple" :disabled="posting" @click="postConfirmOpen = true">
              <span v-if="posting" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <font-awesome-icon v-else icon="book" class="me-1" />
              Post to Accounts
              <font-awesome-icon icon="arrow-right" class="ms-1" />
            </button>
          </template>
          <!-- Approved — show pay button -->
          <template v-else-if="canInitiatePayment">
            <div class="workflow-pill workflow-pill--green">
              <font-awesome-icon icon="check-double" /> Workflow Completed
            </div>
            <button class="btn-initiate btn-initiate--teal" :disabled="paying" @click="payMethodOpen = true; payMethod = runDetails?.country === 'NG' ? 'interswitch' : 'manual'">
              <span v-if="paying" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <font-awesome-icon v-else icon="money-bill-wave" class="me-1" />
              Initiate Payment
              <font-awesome-icon icon="arrow-right" class="ms-1" />
            </button>
          </template>
          <!-- Workflow approved pill only -->
          <div v-else-if="workflowApproved" class="workflow-pill workflow-pill--green">
            <font-awesome-icon icon="check-double" /> Workflow Completed
          </div>
          <!-- In progress -->
          <div v-else-if="workflowSubmitted" class="workflow-pill workflow-pill--amber">
            <font-awesome-icon icon="circle-check" /> Approval In Progress
          </div>
          <!-- Initiate -->
          <button v-else-if="canInitiateWorkflow"
            class="btn-initiate"
            @click="workflowOpen = true">
            <font-awesome-icon icon="sitemap" class="me-1" />
            Initiate Approval
            <font-awesome-icon icon="arrow-right" class="ms-1" />
          </button>
        </div>
      </div>

      <!-- Summary cards -->
      <div class="summary-grid">
        <div class="summary-card">
          <p class="summary-label">Employees</p>
          <p class="summary-val">{{ summary.totalEmployees }}</p>
        </div>
        <div class="summary-card">
          <p class="summary-label">Total Gross</p>
          <p class="summary-val">{{ fmt(summary.totalBase) }}</p>
        </div>
        <div class="summary-card summary-card--red">
          <p class="summary-label">Total Deductions</p>
          <p class="summary-val summary-val--red">{{ fmt(summary.totalStatutoryDeductions + summary.totalAdditionalDeductions) }}</p>
        </div>
        <div class="summary-card summary-card--green">
          <p class="summary-label">Total Net Pay</p>
          <p class="summary-val summary-val--green">{{ fmt(summary.totalNetPay) }}</p>
        </div>
      </div>

      <!-- Entries table -->
      <div class="data-card">
        <div class="data-card-hd">
          <h3 class="data-card-title">Employees in This Payrun</h3>
          <button class="btn-export-trigger" @click="openExport">
            <font-awesome-icon icon="file-export" class="me-1" /> Export
          </button>
        </div>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Gross Pay</th>
                <th>Taxable Gross</th>
                <th>Add. Earning</th>
                <th>Add. Deduction</th>
                <th>Statutory Ded.</th>
                <th>Net Pay</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="emp in entries" :key="emp.id">
                <td>
                  <div class="emp-cell">
                    <div class="emp-avatar">{{ initials(emp?.employee?.first_name+' '+emp?.employee?.last_name) }}</div>
                    <div>
                      <p class="emp-name">{{ emp?.employee?.first_name+' '+emp?.employee?.last_name }}</p>
                      <p class="emp-pos">{{ emp?.employee?.position?.name }}</p>
                    </div>
                  </div>
                </td>
                <td class="text-muted">{{ fmt(emp.gross) }}</td>
                <td class="text-muted">{{ fmt(emp.taxable_income) }}</td>
                <td class="text-earning">+{{ fmt(emp.additional_earning) }}</td>
                <td class="text-red">-{{ fmt(emp.additional_deduction) }}</td>
                <td class="text-orange">-{{ fmt(emp.statutory_deduction) }}</td>
                <td class="text-net">{{ fmt(emp.net) }}</td>
                <!-- <td>
                  <button class="btn-payslip" @click="openPayslip(emp)">
                    <font-awesome-icon icon="file-lines" class="me-1" /> Payslip
                  </button>
                </td> -->
                <td>
                  <router-link :to="$url('/payslip/' + emp.id)" title="View payslip"><button class="btn-payslip" >
                    <font-awesome-icon icon="file-lines" class="me-1" /> Payslip
                  </button></router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </template>


    <!-- ══════════════════════════════
         PAYSLIP MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: payslipOpen }" @click.self="payslipOpen = false">
      <div class="payslip-modal-box">

        <!-- Modal toolbar -->
        <div class="payslip-toolbar">
          <div class="payslip-toolbar-left">
            <font-awesome-icon icon="file-lines" class="toolbar-icon" />
            <span class="toolbar-title">Payslip Preview</span>
          </div>
          <div class="payslip-toolbar-right">
            <button class="btn-download" :disabled="downloading" @click="downloadPayslip">
              <font-awesome-icon :icon="downloading ? 'spinner' : 'download'"
                :class="{ 'fa-spin': downloading }" class="me-1" />
              {{ downloading ? 'Opening...' : 'Download PDF' }}
            </button>
            <button class="btn-toolbar-ghost" @click="payslipOpen = false">
              <font-awesome-icon icon="times" />
            </button>
          </div>
        </div>

        <!-- Payslip content -->
        <div class="payslip-body" v-if="selectedEntry">
          <!-- Green header -->
          <div class="ps-header">
            <div class="ps-header-label">
              <font-awesome-icon icon="file-lines" class="me-1" /> Pay Slip
            </div>
            <div class="ps-employee-row">
              <div class="ps-avatar">{{ initials(selectedEntry?.employee?.first_name+' '+selectedEntry?.employee?.last_name) }}</div>
              <div class="ps-emp-info">
                <p class="ps-name">{{ selectedEntry?.employee?.first_name+' '+selectedEntry?.employee?.last_name }}</p>
                <p class="ps-meta">
                  <font-awesome-icon icon="briefcase" class="me-1" />{{ selectedEntry?.employee?.position?.name }}
                  &nbsp;·&nbsp;
                  <font-awesome-icon icon="building" class="me-1" />{{ selectedEntry?.employee?.department?.name }}
                </p>
                <div class="ps-chips">
                  <span class="ps-chip">ID: {{ selectedEntry?.employee?.employee_no }}</span>
                  <span class="ps-chip">{{ summary.period }}</span>
                </div>
              </div>
            </div>
            <div class="ps-totals-row">
              <div class="ps-total-cell">
                <p class="ps-total-label">Gross Pay</p>
                <p class="ps-total-val">{{ fmt(selectedEntry.gross) }}</p>
              </div>
              <div class="ps-total-cell">
                <p class="ps-total-label">Deductions</p>
                <p class="ps-total-val ps-total-val--red">{{ fmt(selectedEntry.statutory_deduction + selectedEntry.additional_deduction) }}</p>
              </div>
              <div class="ps-total-cell ps-total-cell--bright">
                <p class="ps-total-label">Net Pay</p>
                <p class="ps-total-val">{{ fmt(selectedEntry.net) }}</p>
              </div>
            </div>
          </div>

          <!-- Payslip detail sections -->
          <div class="ps-detail">

            <!-- Salary components -->
            <template v-if="selectedEntry.salaryComponents && selectedEntry.salaryComponents.length">
              <div class="ps-divider">Salary Components</div>
              <div class="ps-line-items">
                <div v-for="(c, i) in selectedEntry.salaryComponents" :key="i" class="ps-line">
                  <span>{{ c.name }}</span><span>{{ fmt(c.amount) }}</span>
                </div>
              </div>
              <div class="ps-subtotal">
                <span>Subtotal</span>
                <span>{{ fmt(selectedEntry.grossSalary - selectedEntry.bonus) }}</span>
              </div>
            </template>

            <!-- Additional earnings -->
            <template v-if="selectedEntry.additionalEarnings && selectedEntry.additionalEarnings.length">
              <div class="ps-divider">Additional Earnings</div>
              <div class="ps-line-items">
                <div v-for="(e, i) in selectedEntry.additionalEarnings" :key="i" class="ps-line">
                  <span>{{ e.name }}</span>
                  <span class="ps-earning">+{{ fmt(e.amount) }}</span>
                </div>
              </div>
              <div class="ps-subtotal ps-subtotal--green">
                <span>Total Earnings</span>
                <span>+{{ fmt(selectedEntry.bonus) }}</span>
              </div>
            </template>

            <!-- Gross pay row -->
            <div class="ps-summary-row">
              <div class="ps-summary-icon ps-summary-icon--blue">
                <font-awesome-icon icon="money-bill-wave" />
              </div>
              <span class="ps-summary-lbl">Gross Pay</span>
              <span class="ps-summary-val">{{ fmt(selectedEntry.grossSalary) }}</span>
            </div>

            <!-- Statutory deductions -->
            <template v-if="statutoryRows(selectedEntry).length">
              <div class="ps-divider">Statutory Deductions</div>
              <div class="ps-line-items">
                <div v-for="(d, i) in statutoryRows(selectedEntry)" :key="i" class="ps-line">
                  <span>{{ d.name }}</span>
                  <span class="ps-orange">-{{ fmt(d.amount) }}</span>
                </div>
              </div>
              <div class="ps-subtotal ps-subtotal--orange">
                <span>Total Statutory</span>
                <span>-{{ fmt(selectedEntry.deductions) }}</span>
              </div>
            </template>

            <!-- Additional deductions -->
            <template v-if="selectedEntry.additionalDeductions && selectedEntry.additionalDeductions.length">
              <div class="ps-divider">Additional Deductions</div>
              <div class="ps-line-items">
                <div v-for="(d, i) in selectedEntry.additionalDeductions" :key="i" class="ps-line">
                  <span>{{ d.name }}</span>
                  <span class="ps-red">-{{ fmt(d.amount) }}</span>
                </div>
              </div>
              <div class="ps-subtotal ps-subtotal--red">
                <span>Total Additional Deductions</span>
                <span>-{{ fmt(selectedEntry.additional_deduction) }}</span>
              </div>
            </template>

            <!-- Taxable gross -->
            <div v-if="selectedEntry.taxableGross > 0" class="ps-summary-row">
              <div class="ps-summary-icon ps-summary-icon--purple">
                <font-awesome-icon icon="scale-balanced" />
              </div>
              <span class="ps-summary-lbl">Taxable Gross</span>
              <span class="ps-summary-val">{{ fmt(selectedEntry.taxableGross) }}</span>
            </div>

            <!-- Net pay banner -->
            <div class="ps-net-banner">
              <div>
                <p class="ps-net-label">Take Home Pay</p>
                <p class="ps-net-val">{{ fmt(selectedEntry.netPay) }}</p>
              </div>
              <div class="ps-net-icon"><font-awesome-icon icon="money-bill-wave" /></div>
            </div>

            <p class="ps-footer-note">
              This is a system-generated payslip and does not require a signature.
            </p>
          </div>
        </div>
      </div>
    </div>


    <!-- ══════════════════════════════
         INITIATE WORKFLOW MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: workflowOpen }" @click.self="!workflowSubmitting && (workflowOpen = false)">
      <div class="modal-box">
        <!-- Header -->
        <div class="workflow-modal-hd">
          <div class="workflow-modal-hd-inner">
            <div class="workflow-hd-icon"><font-awesome-icon icon="sitemap" /></div>
            <div>
              <h3 class="workflow-modal-title">Submit for Approval</h3>
              <p class="workflow-modal-sub">{{ summary.period }} Payroll Run</p>
            </div>
          </div>
          <button class="modal-close-btn" :disabled="workflowSubmitting"
            @click="workflowOpen = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <!-- Body -->
        <div class="modal-bd">
          <!-- Info banner -->
          <div class="info-banner">
            <font-awesome-icon icon="info-circle" class="info-banner-icon" />
            <p>This will initiate the configured payroll approval workflow. Approvers will be notified and can approve or reject this payrun.</p>
          </div>

          <!-- Summary tiles -->
          <div class="workflow-summary-grid">
            <div class="workflow-tile">
              <p class="workflow-tile-label">Employees</p>
              <p class="workflow-tile-val">{{ summary.totalEmployees }}</p>
            </div>
            <div class="workflow-tile">
              <p class="workflow-tile-label">Gross Pay</p>
              <p class="workflow-tile-val">{{ fmt(summary.totalBase) }}</p>
            </div>
            <div class="workflow-tile workflow-tile--green">
              <p class="workflow-tile-label">Net Pay</p>
              <p class="workflow-tile-val workflow-tile-val--green">{{ fmt(summary.totalNetPay) }}</p>
            </div>
          </div>

          <!-- Notes -->
          <div class="field-group">
            <label class="field-label">Notes for Approver <span class="opt">(optional)</span></label>
            <textarea class="field-input" rows="3"
              :placeholder="`e.g. ${summary.period} payroll run — please review and approve.`"
              v-model="workflowNotes"
              :disabled="workflowSubmitting"></textarea>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-ft">
          <button class="btn-ghost" :disabled="workflowSubmitting" @click="workflowOpen = false">Cancel</button>
          <button class="btn-primary-action" :disabled="workflowSubmitting" @click="submitWorkflow">
            <span v-if="workflowSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <font-awesome-icon v-else icon="check-double" class="me-1" />
            {{ workflowSubmitting ? 'Submitting...' : 'Submit for Approval' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════
         INITIATE PAYMENT METHOD MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: payMethodOpen }" @click.self="!paying && (payMethodOpen = false)">
      <div class="modal-box modal-box--sm">
        <div class="workflow-modal-hd">
          <div class="workflow-modal-hd-inner">
            <div class="workflow-hd-icon workflow-hd-icon--teal"><font-awesome-icon icon="money-bill-wave" /></div>
            <div>
              <h3 class="workflow-modal-title">Initiate Payment</h3>
              <p class="workflow-modal-sub">{{ summary.period }} Payroll Run</p>
            </div>
          </div>
          <button class="modal-close-btn" :disabled="paying" @click="payMethodOpen = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <div class="modal-bd">
          <!-- Payment method cards -->
          <div class="pay-method-cards">
            <button
              v-if="runDetails?.country === 'NG'"
              class="pay-method-card"
              :class="{ selected: payMethod === 'interswitch' }"
              @click="payMethod = 'interswitch'"
              :disabled="paying">
              <div class="pay-method-card-icon pay-method-card-icon--teal">
                <font-awesome-icon icon="money-bill-wave" />
              </div>
              <div class="pay-method-card-body">
                <p class="pay-method-card-title">Make Payment</p>
                <p class="pay-method-card-desc">Disburse salaries via Interswitch wallet transfer</p>
              </div>
              <div class="pay-method-check" v-if="payMethod === 'interswitch'">
                <font-awesome-icon icon="check-circle" />
              </div>
            </button>

            <button
              class="pay-method-card"
              :class="{ selected: payMethod === 'manual' }"
              @click="payMethod = 'manual'"
              :disabled="paying">
              <div class="pay-method-card-icon pay-method-card-icon--green">
                <font-awesome-icon icon="check-circle" />
              </div>
              <div class="pay-method-card-body">
                <p class="pay-method-card-title">Mark as Paid</p>
                <p class="pay-method-card-desc">Manually confirm salaries have been disbursed</p>
              </div>
              <div class="pay-method-check" v-if="payMethod === 'manual'">
                <font-awesome-icon icon="check-circle" />
              </div>
            </button>
          </div>

          <!-- Summary -->
          <div class="workflow-summary-grid">
            <div class="workflow-tile">
              <p class="workflow-tile-label">Employees</p>
              <p class="workflow-tile-val">{{ summary.totalEmployees }}</p>
            </div>
            <div class="workflow-tile workflow-tile--green">
              <p class="workflow-tile-label">Total Net Pay</p>
              <p class="workflow-tile-val workflow-tile-val--green">{{ fmt(summary.totalNetPay) }}</p>
            </div>
          </div>

          <!-- Notes (manual only) -->
          <div v-if="payMethod === 'manual'" class="field-group">
            <label class="field-label">Notes <span class="opt">(optional)</span></label>
            <textarea class="field-input" rows="2"
              placeholder="e.g. Paid via bank transfer on 30 Oct 2026"
              v-model="markPaidNotes"
              :disabled="paying"></textarea>
          </div>
        </div>

        <div class="modal-ft">
          <button class="btn-ghost" :disabled="paying" @click="payMethodOpen = false">Cancel</button>
          <button class="btn-primary-action btn-primary-action--teal" :disabled="paying" @click="initiatePayment">
            <span v-if="paying" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <font-awesome-icon v-else icon="money-bill-wave" class="me-1" />
            {{ paying ? 'Processing...' : (payMethod === 'manual' ? 'Mark as Paid' : 'Confirm Payment') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════
         POST TO ACCOUNTS CONFIRM MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: postConfirmOpen }" @click.self="!posting && (postConfirmOpen = false)">
      <div class="modal-box modal-box--sm">
        <div class="workflow-modal-hd">
          <div class="workflow-modal-hd-inner">
            <div class="workflow-hd-icon workflow-hd-icon--purple"><font-awesome-icon icon="book" /></div>
            <div>
              <h3 class="workflow-modal-title">Post to Accounts</h3>
              <p class="workflow-modal-sub">{{ summary.period }} Payroll Run</p>
            </div>
          </div>
          <button class="modal-close-btn" :disabled="posting" @click="postConfirmOpen = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <div class="modal-bd">
          <div class="info-banner info-banner--purple">
            <font-awesome-icon icon="info-circle" class="info-banner-icon" />
            <p>This will post payroll journal entries to the general ledger and send payslips to all employees. <strong>This action cannot be undone.</strong></p>
          </div>
          <div class="workflow-summary-grid">
            <div class="workflow-tile">
              <p class="workflow-tile-label">Employees</p>
              <p class="workflow-tile-val">{{ summary.totalEmployees }}</p>
            </div>
            <div class="workflow-tile workflow-tile--green">
              <p class="workflow-tile-label">Total Net Pay</p>
              <p class="workflow-tile-val workflow-tile-val--green">{{ fmt(summary.totalNetPay) }}</p>
            </div>
          </div>
        </div>

        <div class="modal-ft">
          <button class="btn-ghost" :disabled="posting" @click="postConfirmOpen = false">Cancel</button>
          <button class="btn-primary-action btn-primary-action--purple" :disabled="posting" @click="initiatePosting">
            <span v-if="posting" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <font-awesome-icon v-else icon="book" class="me-1" />
            {{ posting ? 'Posting...' : 'Confirm & Post' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════
         EXPORT MODAL
    ══════════════════════════════ -->
    <div class="modal-overlay" :class="{ open: exportOpen }" @click.self="exportOpen = false">
      <div class="modal-box modal-box--xl">

        <div class="workflow-modal-hd">
          <div class="workflow-modal-hd-inner">
            <div class="workflow-hd-icon"><font-awesome-icon icon="file-export" /></div>
            <div>
              <h3 class="workflow-modal-title">Export Payroll Data</h3>
              <p class="workflow-modal-sub">{{ summary.period }} · {{ summary.totalEmployees }} employees</p>
            </div>
          </div>
          <button class="modal-close-btn" @click="exportOpen = false"><font-awesome-icon icon="times" /></button>
        </div>

        <!-- Loading state -->
        <div v-if="exportLoading" class="modal-bd" style="align-items:center;justify-content:center;padding:48px;flex-direction:row;gap:12px;">
          <div class="spinner-border spinner-border-sm" role="status"></div>
          <span style="font-size:13px;color:#7a90b0;">Loading export data...</span>
        </div>

        <!-- Column picker -->
        <div v-else class="modal-bd export-modal-bd">
          <div class="export-toolbar">
            <span class="export-hint">Select the columns to include in your export</span>
            <div class="export-toolbar-actions">
              <button class="btn-xs-outline" @click="selectAllCols">Select All</button>
              <button class="btn-xs-outline" @click="deselectAllCols">Deselect All</button>
            </div>
          </div>

          <div class="export-groups">
            <div v-for="group in columnGroups" :key="group.key" class="export-group">
              <div class="export-group-hd">
                <span class="export-group-label">{{ group.label }}</span>
                <button class="btn-xs-link" @click="toggleGroup(group.key)">
                  {{ isGroupAllSelected(group) ? 'Deselect all' : 'Select all' }}
                </button>
              </div>
              <div class="export-cols">
                <label v-for="col in group.columns" :key="col.key" class="export-col-item">
                  <input type="checkbox" :value="col.key" v-model="selectedCols" />
                  <span>{{ col.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!exportLoading" class="modal-ft">
          <button class="btn-ghost" @click="exportOpen = false">Cancel</button>
          <button class="btn-export-csv" :disabled="!selectedCols.length" @click="doExportCSV">
            <font-awesome-icon icon="file-alt" class="me-1" /> Export CSV
          </button>
          <button class="btn-export-pdf" :disabled="!selectedCols.length" @click="doExportPDF">
            <font-awesome-icon icon="file-pdf" class="me-1" /> Export PDF
          </button>
        </div>

      </div>
    </div>

    <!-- Backdrop -->
    <div v-if="payslipOpen || workflowOpen || payMethodOpen || postConfirmOpen || exportOpen" class="modal-backdrop-custom" />

  </div>
</template>


<script>
import Header from './component/header.vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default {
  name: 'PayrunDetailsPage',

  components: { Header },

  data() {
    return {
      loading:   true,
      runDetails: null,
      entries:   [],

      // Payslip
      payslipOpen:   false,
      selectedEntry: null,
      downloading:   false,

      // Workflow modal
      workflowOpen:       false,
      workflowNotes:      '',
      workflowSubmitting: false,

      // Payment / posting
      payMethodOpen:   false,
      payMethod:       'interswitch',
      markPaidNotes:   '',
      postConfirmOpen: false,
      paying:          false,
      posting:         false,

      // Export
      exportOpen:    false,
      exportLoading: false,
      exportRows:    [],
      selectedCols: [
        'name', 'employee_no', 'department', 'status',
        'bank_name', 'bank_code', 'bank_account_name', 'bank_account_no', 'bank_sortcode',
        'pension_provider', 'pension_number',
        'basic', 'gross', 'net',
        'tax', 'pension_deduction', 'statutory_deduction',
      ],
      columnGroups: [
        {
          key: 'employee', label: 'Employee Info',
          columns: [
            { key: 'name',        label: 'Name' },
            { key: 'employee_no', label: 'Employee No' },
            { key: 'department',  label: 'Department' },
            { key: 'position',    label: 'Position' },
            { key: 'salary_type', label: 'Salary Type' },
            { key: 'status',      label: 'Status' },
          ],
        },
        {
          key: 'bank', label: 'Bank Details',
          columns: [
            { key: 'bank_name',         label: 'Bank Name' },
            { key: 'bank_code',         label: 'Bank Code' },
            { key: 'bank_account_name', label: 'Account Name' },
            { key: 'bank_account_no',   label: 'Account No' },
            { key: 'bank_sortcode',     label: 'Sort Code' },
          ],
        },
        {
          key: 'pension', label: 'Pension Details',
          columns: [
            { key: 'pension_provider', label: 'Pension Provider' },
            { key: 'pension_code',     label: 'Pension Code' },
            { key: 'pension_number',   label: 'Pension Acct No' },
          ],
        },
        {
          key: 'salary', label: 'Salary',
          columns: [
            { key: 'basic',          label: 'Basic Pay' },
            { key: 'gross',          label: 'Gross Pay' },
            { key: 'taxable_income', label: 'Taxable Gross' },
            { key: 'net',            label: 'Net Pay' },
          ],
        },
        {
          key: 'statutory', label: 'Statutory Deductions',
          columns: [
            { key: 'tax',                 label: 'PAYE' },
            { key: 'pension_deduction',   label: 'Employee Pension' },
            { key: 'employer_pension',    label: 'Employer Pension' },
            { key: 'cra',                 label: 'CRA' },
            { key: 'nsitf',               label: 'NSITF' },
            { key: 'nhf',                 label: 'NHF' },
            { key: 'nhis',                label: 'NHIS' },
            { key: 'employee_ni',         label: 'Employee NI' },
            { key: 'employer_ni',         label: 'Employer NI' },
            { key: 'statutory_deduction', label: 'Total Statutory' },
          ],
        },
        {
          key: 'additional', label: 'Additional',
          columns: [
            { key: 'additional_earning',   label: 'Additional Earnings' },
            { key: 'additional_deduction', label: 'Additional Deductions' },
          ],
        },
        {
          key: 'attendance', label: 'Attendance & Hours',
          columns: [
            { key: 'total_hours',    label: 'Total Hours' },
            { key: 'hourly_rate',    label: 'Hourly Rate' },
            { key: 'overtime_hours', label: 'OT Hours' },
            { key: 'overtime_pay',   label: 'OT Pay' },
            { key: 'lwp_days',       label: 'LWP Days' },
            { key: 'lwp_deduction',  label: 'LWP Deduction' },
          ],
        },
      ],

      toast: useToast(),
    };
  },

  computed: {
    payrunId() {
      return this.$route.params.id;
    },

    summary() {
      const totalEmployees = this.entries.length;
      // Debug: log first entry to inspect raw field names from API
      if (this.entries.length && process.env.NODE_ENV !== 'production') {
        console.log('[Payrun summary] first entry keys:', Object.keys(this.entries[0]));
        console.log('[Payrun summary] first entry:', this.entries[0]);
      }
      const toNum = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
      const totalBase       = this.entries.reduce((s, e) => s + toNum(e.gross ?? 0), 0);
      const totalStatutoryDeductions = this.entries.reduce((s, e) => s + toNum(e.statutory_deduction ?? 0), 0);
      const totalAdditionalDeductions = this.entries.reduce((s, e) => s + toNum(e.additional_deduction ?? 0), 0);
      const totalNetPay     = this.entries.reduce((s, e) => s + toNum(e.net ?? 0), 0);

      const startDate = this.runDetails?.end_date
        ? new Date(this.runDetails.end_date) : null;
      const period    = startDate
        ? startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        : 'N/A';
      const runDate   = this.runDetails?.created_at
        ? new Date(this.runDetails.created_at).toLocaleDateString()
        : 'N/A';

      return { period, runDate, totalEmployees, totalBase, totalStatutoryDeductions, totalAdditionalDeductions, totalNetPay,
               status: (this.runDetails?.status || 'open').toLowerCase() };
    },

    canInitiateWorkflow() {
      return ['processed'].includes(this.summary.status);
    },

    workflowSubmitted() {
      return ['in_workflow'].includes(this.summary.status);
    },

    workflowApproved() {
      return ['approved', 'paid', 'posted'].includes(this.summary.status);
    },

    canInitiatePayment() {
      return this.summary.status === 'approved';
    },

    canInitiatePosting() {
      return this.summary.status === 'paid';
    },

    currencySymbol() {
      const map = { GB: '£', US: '$', EU: '€' };
      return map[this.runDetails?.country] || '₦';
    },

    currencyLocale() {
      const map = { GB: 'en-GB', US: 'en-US' };
      return map[this.runDetails?.country] || 'en-NG';
    },
  },

  created() {
    this.fetchAll();
  },

  methods: {
    // -- Data ----------------------------------------------------------
    async fetchAll() {
      this.loading = true;
      try {
        const [runRes, entriesRes] = await Promise.all([
          axios.get(`/payroll/period/${this.payrunId}`),
          axios.get(`/payroll/period/payslip/${this.payrunId}`),
        ]);

        this.runDetails = runRes.data?.data || runRes.data || {};

        this.entries = entriesRes.data?.data?.data?.data || entriesRes.data?.data?.data || entriesRes.data?.data || [];
      } catch {
        this.toast.error('Could not load payrun details.');
      } finally {
        this.loading = false;
      }
    },

    mapEntry(entry, index = 0) {
      const employee  = entry?.employee || {};
      const firstName = entry?.user?.first_name  || employee?.firstname || '';
      const lastName  = entry?.user?.last_name   || employee?.lastname  || '';
      const fullName  = entry?.employee_name
        || `${firstName} ${lastName}`.trim()
        || 'Unknown Employee';

      const parse = (val) => { try { return JSON.parse(val || '[]'); } catch { return []; } };

      return {
        id:                   entry?.id || entry?.reference || `emp-${index}`,
        employeeId:           String(entry?.reference || employee?.id || '--'),
        name:                 fullName,
        department:           employee?.department?.name || '--',
        position:             employee?.position?.name  || entry?.user?.job_title?.name || '--',
        grossSalary:          Number(entry?.gross_pay              ?? 0),
        taxableGross:         Number(entry?.taxable_gross          ?? 0),
        bonus:                Number(entry?.total_additional_earning  ?? 0),
        additional_deduction: Number(entry?.total_additional_deduction ?? 0),
        deductions:           Number(entry?.total_deductions        ?? 0),
        netPay:               Number(entry?.net_pay                 ?? 0),
        totalTax:             Number(entry?.total_tax               ?? 0),
        totalPension:         Number(entry?.total_pension           ?? 0),
        totalNhf:             Number(entry?.total_nhf               ?? 0),
        totalNhis:            Number(entry?.total_nhis              ?? 0),
        status:               entry?.status || 'Pending',
        salaryComponents:     parse(entry?.salary_components),
        additionalEarnings:   parse(entry?.additional_earnings),
        additionalDeductions: parse(entry?.additional_deductions),
        statutoryDeductions:  parse(entry?.statutory_deductions),
      };
    },

    // -- Payslip -------------------------------------------------------
    openPayslip(emp) {
      this.selectedEntry = emp;
      this.payslipOpen   = true;
    },

    statutoryRows(entry) {
      if (entry.statutoryDeductions?.length) return entry.statutoryDeductions;
      return [
        entry.totalTax     > 0 && { name: 'PAYE (Income Tax)', amount: entry.totalTax },
        entry.totalPension > 0 && { name: 'Pension',           amount: entry.totalPension },
        entry.totalNhf     > 0 && { name: 'NHF',               amount: entry.totalNhf },
        entry.totalNhis    > 0 && { name: 'NHIS',              amount: entry.totalNhis },
      ].filter(Boolean);
    },

    async downloadPayslip() {
      if (!this.selectedEntry) return;
      this.downloading = true;
      try {
        const entry  = this.selectedEntry;
        const period = this.summary.period;
        const statRows = this.statutoryRows(entry);
        const totalAllDed = entry.statutory_deduction + entry.additional_deduction;

        // Build HTML for print window
        const row = (label, value, color = '#374151', bg = '#fff') =>
          `<tr>
            <td style="padding:8px 16px;font-size:12.5px;color:#6b7280;background:${bg};">${label}</td>
            <td style="padding:8px 16px;font-size:12.5px;color:${color};font-weight:600;text-align:right;background:${bg};">${value}</td>
          </tr>`;

        const section = (title, titleColor, footerBg, rows, footerLabel, footerVal, footerColor) =>
          rows ? `<div style="margin-bottom:14px;">
            <div style="text-align:center;margin-bottom:6px;">
              <span style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.14em;color:#9ca3af;">${title}</span>
            </div>
            <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
              <tbody>${rows}</tbody>
              <tfoot><tr>
                <td style="padding:8px 16px;font-size:10px;font-weight:700;text-transform:uppercase;color:${titleColor};background:${footerBg};">${footerLabel}</td>
                <td style="padding:8px 16px;font-size:12.5px;font-weight:700;color:${footerColor};text-align:right;background:${footerBg};">${footerVal}</td>
              </tr></tfoot>
            </table></div>` : '';

        const currSym    = this.currencySymbol;
        const currLocale = this.currencyLocale;
        const fmt2 = (v) => currSym + Number(v || 0).toLocaleString(currLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const initI = (name) => name.split(' ').map(w => w[0] || '').join('').toUpperCase().slice(0, 2);

        const salaryRows    = (entry.salaryComponents || []).map((c, i) => row(c.name, fmt2(c.amount), '#374151', i % 2 ? '#f9fafb' : '#fff')).join('');
        const earningRows   = (entry.additionalEarnings || []).map((e, i) => row(e.name, `+ ${fmt2(e.amount)}`, '#059669', i % 2 ? '#f9fafb' : '#fff')).join('');
        const statRowsHtml  = statRows.map((d, i) => row(d.name, `- ${fmt2(d.amount)}`, '#ea580c', i % 2 ? '#f9fafb' : '#fff')).join('');
        const addDedRowsHtml = (entry.additionalDeductions || []).map((d, i) => row(d.name, `- ${fmt2(d.amount)}`, '#dc2626', i % 2 ? '#f9fafb' : '#fff')).join('');

        const GREEN = '#166534';
        const avatarDiv = `<div style="width:52px;height:52px;border-radius:12px;background:rgba(255,255,255,.18);border:2px solid rgba(255,255,255,.35);display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:700;color:#fff;">${initI(entry.name)}</div>`;

        const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
          <style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;background:#fff;width:794px;}
          @media print{*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}@page{size:A4 portrait;margin:0;}}</style>
          </head><body>
          <div style="background:${GREEN};padding:22px 28px 20px;">
            <div style="font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.18em;color:rgba(255,255,255,.65);margin-bottom:14px;">Pay Slip</div>
            <div style="display:flex;align-items:flex-start;gap:14px;margin-bottom:18px;">
              ${avatarDiv}
              <div>
                <div style="font-size:17px;font-weight:700;color:#fff;margin-bottom:3px;">${entry?.employee?.first_name+' '+entry?.employee?.last_name}</div>
                <div style="font-size:11.5px;color:rgba(255,255,255,.65);margin-bottom:8px;">${entry.position} &nbsp;·&nbsp; ${entry.department}</div>
                <div style="display:flex;gap:8px;">
                  <span style="background:rgba(255,255,255,.18);border-radius:5px;padding:2px 10px;font-size:9.5px;font-weight:600;color:rgba(255,255,255,.9);">ID: ${entry.employeeId}</span>
                  <span style="background:rgba(255,255,255,.18);border-radius:5px;padding:2px 10px;font-size:9.5px;font-weight:600;color:rgba(255,255,255,.9);">${period}</span>
                </div>
              </div>
            </div>
            <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid rgba(255,255,255,.2);border-radius:10px;overflow:hidden;">
              <tr>
                <td style="background:rgba(255,255,255,.12);padding:11px 16px;text-align:center;border-right:1px solid rgba(255,255,255,.15);">
                  <div style="font-size:8px;font-weight:700;text-transform:uppercase;color:rgba(255,255,255,.55);margin-bottom:4px;">Gross Pay</div>
                  <div style="font-size:13.5px;font-weight:700;color:#fff;">${fmt2(entry.grossSalary)}</div>
                </td>
                <td style="background:rgba(255,255,255,.12);padding:11px 16px;text-align:center;border-right:1px solid rgba(255,255,255,.15);">
                  <div style="font-size:8px;font-weight:700;text-transform:uppercase;color:rgba(255,255,255,.55);margin-bottom:4px;">Deductions</div>
                  <div style="font-size:13.5px;font-weight:700;color:#fca5a5;">${fmt2(totalAllDed)}</div>
                </td>
                <td style="background:rgba(255,255,255,.22);padding:11px 16px;text-align:center;">
                  <div style="font-size:8px;font-weight:700;text-transform:uppercase;color:rgba(255,255,255,.55);margin-bottom:4px;">Net Pay</div>
                  <div style="font-size:13.5px;font-weight:700;color:#fff;">${fmt2(entry.netPay)}</div>
                </td>
              </tr>
            </table>
          </div>
          <div style="padding:20px 28px;">
            ${section('Salary Components','#6b7280','#f3f4f6', salaryRows || null,'Subtotal',fmt2(entry.grossSalary - entry.bonus),'#111827')}
            ${section('Additional Earnings','#059669','#ecfdf5', earningRows || null,'Total Earnings','+ '+fmt2(entry.bonus),'#059669')}
            <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e5e7eb;border-radius:10px;margin-bottom:14px;">
              <tr><td style="padding:10px 16px;font-size:12.5px;font-weight:600;color:#374151;">Gross Pay</td>
              <td style="padding:10px 16px;font-size:14px;font-weight:700;color:#111827;text-align:right;">${fmt2(entry.grossSalary)}</td></tr>
            </table>
            ${section('Statutory Deductions','#ea580c','#fff7ed', statRowsHtml || null,'Total Statutory','- '+fmt2(entry.deductions),'#ea580c')}
            ${section('Additional Deductions','#dc2626','#fef2f2', addDedRowsHtml || null,'Total Additional Deductions','- '+fmt2(entry.additional_deduction),'#dc2626')}
            <table width="100%" cellpadding="0" cellspacing="0" style="border-radius:12px;overflow:hidden;margin-bottom:14px;">
              <tr><td style="background:${GREEN};padding:18px 22px;vertical-align:middle;">
                <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.18em;color:rgba(255,255,255,.6);margin-bottom:5px;">Take Home Pay</div>
                <div style="font-size:28px;font-weight:800;color:#fff;line-height:1;">${fmt2(entry.netPay)}</div>
              </td>
              <td style="background:${GREEN};padding:18px 22px;text-align:right;font-size:34px;">💰</td></tr>
            </table>
            <div style="text-align:center;font-size:10px;color:#9ca3af;padding-bottom:12px;">
              This is a system-generated payslip and does not require a signature.
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 1500);
            };
          <' + '/script>
          </body></html>`;

        const win = window.open('', '_blank', 'width=900,height=750');
        if (!win) { this.toast.error('Please allow popups to download the payslip.'); return; }
        win.document.open();
        win.document.write(html);
        win.document.close();
      } finally {
        setTimeout(() => { this.downloading = false; }, 600);
      }
    },

    // -- Payment & Posting ---------------------------------------------
    initiatePayment() {
      this.paying = true;
      const request = this.payMethod === 'manual'
        ? axios.post(`/payroll/period/${this.payrunId}/mark-disbursed`, { notes: this.markPaidNotes })
        : axios.get(`/payroll/period/pay/${this.payrunId}`);

      request
        .then(() => {
          const msg = this.payMethod === 'manual'
            ? 'Payroll marked as paid successfully.'
            : 'Salary payments initiated successfully.';
          this.toast.success(msg);
          this.payMethodOpen = false;
          this.markPaidNotes = '';
          this.fetchAll();
        })
        .catch(err => {
          const data = err.response?.data;
          const errors = data?.errors ?? data?.data?.errors;
          if (errors && errors.length) {
            errors.forEach(e => this.toast.error(e, { timeout: 8000 }));
          } else {
            this.toast.error(data?.message ?? 'Failed to initiate payment.');
          }
        })
        .finally(() => { this.paying = false; });
    },

    initiatePosting() {
      this.posting = true;
      axios.get(`/payroll/period/post/${this.payrunId}`)
        .then(() => {
          this.toast.success('Payroll posted to accounts and payslips sent.');
          this.postConfirmOpen = false;
          this.fetchAll();
        })
        .catch(err => this.toast.error(err.response?.data?.message ?? 'Failed to post payroll.'))
        .finally(() => { this.posting = false; });
    },

    // -- Workflow ------------------------------------------------------
    submitWorkflow() {
      this.workflowSubmitting = true;
      axios.post('/approvals/submit', {
        module:           'payroll',
        module_id:        this.runDetails?.id ?? this.payrunId,
        module_reference: this.runDetails?.reference ?? this.payrunId,
        notes:            this.workflowNotes.trim()
          || `Approval request for ${this.summary.period} payroll run.`,
      })
        .then(() => {
          this.toast.success('Payroll submitted for approval successfully.');
          this.workflowOpen  = false;
          this.workflowNotes = '';
          this.fetchAll();
        })
        .catch(err => {
          this.toast.error(err.response?.data?.message ?? 'Failed to submit for approval.');
        })
        .finally(() => { this.workflowSubmitting = false; });
    },

    // -- Helpers -------------------------------------------------------
    fmt(v) {
      return this.currencySymbol + Number(v || 0).toLocaleString(this.currencyLocale, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    },

    initials(name) {
      return (name || '').split(' ').map(w => w[0] || '').join('').toUpperCase().slice(0, 2) || '?';
    },

    // -- Export --------------------------------------------------------
    async openExport() {
      this.exportOpen = true;
      if (this.exportRows.length) return;
      this.exportLoading = true;
      try {
        const res = await axios.get(`/payroll/period/${this.payrunId}/export`);
        this.exportRows = res.data?.data?.items || [];
      } catch {
        this.toast.error('Could not load export data.');
        this.exportOpen = false;
      } finally {
        this.exportLoading = false;
      }
    },

    getOrderedSelectedCols() {
      return this.columnGroups
        .flatMap(g => g.columns)
        .filter(c => this.selectedCols.includes(c.key));
    },

    isGroupAllSelected(group) {
      return group.columns.every(c => this.selectedCols.includes(c.key));
    },

    toggleGroup(groupKey) {
      const group = this.columnGroups.find(g => g.key === groupKey);
      if (!group) return;
      if (this.isGroupAllSelected(group)) {
        this.selectedCols = this.selectedCols.filter(k => !group.columns.some(c => c.key === k));
      } else {
        const toAdd = group.columns.map(c => c.key).filter(k => !this.selectedCols.includes(k));
        this.selectedCols = [...this.selectedCols, ...toAdd];
      }
    },

    selectAllCols() {
      this.selectedCols = this.columnGroups.flatMap(g => g.columns.map(c => c.key));
    },

    deselectAllCols() {
      this.selectedCols = [];
    },

    MONEY_KEYS_SET() {
      return new Set([
        'basic', 'gross', 'taxable_income', 'net', 'tax', 'pension_deduction',
        'employer_pension', 'cra', 'nsitf', 'nhf', 'nhis', 'employee_ni',
        'employer_ni', 'statutory_deduction', 'additional_earning', 'additional_deduction',
        'overtime_pay', 'lwp_deduction', 'hourly_rate',
      ]);
    },

    isMoney(key) {
      return this.MONEY_KEYS_SET().has(key);
    },

    getCellValue(item, key) {
      const emp     = item.employee || {};
      const bank    = emp.bank || {};
      const pension = emp.pension_company || {};
      const map = {
        name:                 ((emp.first_name || '') + ' ' + (emp.last_name || '')).trim(),
        employee_no:          emp.employee_no || '',
        department:           emp.department?.name || '',
        position:             emp.position?.name || '',
        salary_type:          emp.salary_type || '',
        status:               (this.summary.status || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        bank_name:            bank.name || '',
        bank_code:            bank.code || '',
        bank_account_name:    emp.bank_account_name || '',
        bank_account_no:      emp.bank_account_no || '',
        bank_sortcode:        emp.bank_sortcode || '',
        pension_provider:     pension.name || '',
        pension_code:         pension.code || '',
        pension_number:       emp.pension_number || '',
        basic:                Number(item.basic          || 0),
        gross:                Number(item.gross          || 0),
        taxable_income:       Number(item.taxable_income || 0),
        net:                  Number(item.net            || 0),
        tax:                  Number(item.tax            || 0),
        pension_deduction:    Number(item.pension        || 0),
        employer_pension:     Number(item.employer_pension    || 0),
        cra:                  Number(item.cra            || 0),
        nsitf:                Number(item.nsitf          || 0),
        nhf:                  Number(item.nhf            || 0),
        nhis:                 Number(item.nhis           || 0),
        employee_ni:          Number(item.employee_ni    || 0),
        employer_ni:          Number(item.employer_ni    || 0),
        statutory_deduction:  Number(item.statutory_deduction  || 0),
        additional_earning:   Number(item.additional_earning   || 0),
        additional_deduction: Number(item.additional_deduction || 0),
        total_hours:          Number(item.total_hours    || 0),
        hourly_rate:          Number(item.hourly_rate    || 0),
        overtime_hours:       Number(item.overtime_hours || 0),
        overtime_pay:         Number(item.overtime_pay   || 0),
        lwp_days:             Number(item.lwp_days       || 0),
        lwp_deduction:        Number(item.lwp_deduction  || 0),
      };
      return map[key] ?? '';
    },

    doExportCSV() {
      const cols = this.getOrderedSelectedCols();
      const period = this.summary.period.replace(/\s/g, '-');

      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

      const headers = cols.map(c => escape(c.label)).join(',');

      const rows = this.exportRows.map(item => {
        return cols.map(c => {
          const val = this.getCellValue(item, c.key);
          return this.isMoney(c.key) ? Number(val).toFixed(2) : escape(val);
        }).join(',');
      });

      // Totals row
      const totalsRow = cols.map(c => {
        if (this.isMoney(c.key)) {
          const sum = this.exportRows.reduce((s, item) => s + Number(this.getCellValue(item, c.key) || 0), 0);
          return sum.toFixed(2);
        }
        return c.key === 'name' ? escape('TOTALS') : '""';
      }).join(',');

      const csv = [headers, ...rows, totalsRow].join('\r\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll-${period}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    doExportPDF() {
      const cols      = this.getOrderedSelectedCols();
      const period    = this.summary.period;
      const runDate   = this.summary.runDate;
      const totalEmps = this.exportRows.length;
      const generated = new Date().toLocaleString();

      // jsPDF standard Helvetica cannot render the NGN sign (U+20A6) — use plain numbers
      const fmtNum  = (v) => Number(v || 0).toLocaleString('en-NG', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
      const fmtCell = (key, val) => this.isMoney(key)
        ? fmtNum(val)
        : (String(val ?? '') || '-');

      // Per-column totals
      const totals = {};
      cols.forEach(c => {
        if (this.isMoney(c.key)) {
          totals[c.key] = this.exportRows.reduce(
            (s, item) => s + Number(this.getCellValue(item, c.key) || 0), 0
          );
        }
      });

      const head = [cols.map(c => c.label)];
      const body = this.exportRows.map(item =>
        cols.map(c => fmtCell(c.key, this.getCellValue(item, c.key)))
      );
      const foot = [cols.map(c => {
        if (totals[c.key] != null) return fmtNum(totals[c.key]);
        return c.key === 'name' ? 'TOTALS' : '';
      })];

      // Per-column preferred widths (mm) and alignment
      const colCfg = {
        name:                 { w: 34, align: 'left'  },
        employee_no:          { w: 17, align: 'left'  },
        department:           { w: 22, align: 'left'  },
        position:             { w: 22, align: 'left'  },
        salary_type:          { w: 17, align: 'left'  },
        status:               { w: 18, align: 'left'  },
        bank_name:            { w: 26, align: 'left'  },
        bank_code:            { w: 14, align: 'left'  },
        bank_account_name:    { w: 28, align: 'left'  },
        bank_account_no:      { w: 22, align: 'left'  },
        bank_sortcode:        { w: 16, align: 'left'  },
        pension_provider:     { w: 26, align: 'left'  },
        pension_code:         { w: 14, align: 'left'  },
        pension_number:       { w: 22, align: 'left'  },
        basic:                { w: 26, align: 'right' },
        gross:                { w: 26, align: 'right' },
        taxable_income:       { w: 24, align: 'right' },
        net:                  { w: 26, align: 'right' },
        tax:                  { w: 22, align: 'right' },
        pension_deduction:    { w: 22, align: 'right' },
        employer_pension:     { w: 22, align: 'right' },
        cra:                  { w: 20, align: 'right' },
        nsitf:                { w: 20, align: 'right' },
        nhf:                  { w: 20, align: 'right' },
        nhis:                 { w: 20, align: 'right' },
        employee_ni:          { w: 20, align: 'right' },
        employer_ni:          { w: 20, align: 'right' },
        statutory_deduction:  { w: 24, align: 'right' },
        additional_earning:   { w: 24, align: 'right' },
        additional_deduction: { w: 24, align: 'right' },
        total_hours:          { w: 14, align: 'right' },
        hourly_rate:          { w: 20, align: 'right' },
        overtime_hours:       { w: 14, align: 'right' },
        overtime_pay:         { w: 22, align: 'right' },
        lwp_days:             { w: 14, align: 'right' },
        lwp_deduction:        { w: 22, align: 'right' },
      };

      const margin = 10;
      // A4 landscape = 297mm wide, A3 = 420mm wide
      const a4Avail = 297 - margin * 2;
      const a3Avail = 420 - margin * 2;

      const totalPreferred = cols.reduce((s, c) => s + (colCfg[c.key]?.w || 20), 0);

      // Pick smallest page that fits; scale down if even A3 isn't enough
      const format = totalPreferred <= a4Avail ? 'a4' : 'a3';
      const availW = format === 'a3' ? a3Avail : a4Avail;
      const scale  = totalPreferred > availW ? availW / totalPreferred : 1;

      const doc   = new jsPDF({ orientation: 'landscape', unit: 'mm', format });
      const pageW = doc.internal.pageSize.getWidth();

      // Title block
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(26, 43, 69);
      doc.text(`Payroll Export - ${period}`, 14, 12);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Run date: ${runDate}  |  ${totalEmps} employee(s)  |  Generated: ${generated}`,
        14, 19
      );
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text('CONFIDENTIAL', pageW - 14, 12, { align: 'right' });

      // Scale column widths to guarantee they fit within the page
      const columnStyles = {};
      cols.forEach((c, i) => {
        const cfg = colCfg[c.key] || { w: 20, align: 'left' };
        columnStyles[i] = {
          cellWidth: Math.max(8, Math.floor(cfg.w * scale * 10) / 10),
          halign: cfg.align,
        };
      });

      // Shrink font proportionally when columns are squeezed
      const fontSize = scale < 0.55 ? 5.5 : scale < 0.7 ? 6 : scale < 0.85 ? 6.5 : 7.5;

      autoTable(doc, {
        head,
        body,
        foot,
        startY: 24,
        margin: { left: margin, right: margin },
        tableWidth: availW,
        styles: {
          fontSize,
          cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
          overflow: 'ellipsize',
          textColor: [55, 65, 81],
          lineColor: [226, 232, 240],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [0, 74, 173],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize,
          halign: 'center',
        },
        footStyles: {
          fillColor: [238, 244, 255],
          textColor: [26, 43, 69],
          fontStyle: 'bold',
          fontSize,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles,
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const w = doc.internal.pageSize.getWidth();
          const h = doc.internal.pageSize.getHeight();
          doc.setFontSize(6.5);
          doc.setTextColor(156, 163, 175);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, w - 10, h - 5, { align: 'right' });
          doc.text(`Payroll Export - ${period}`, 10, h - 5);
        },
      });

      doc.save(`payroll-${period}.pdf`);
    },

    statusClass(status) {
      const map = {
        approved:    'status--green',
        paid:        'status--teal',
        posted:      'status--purple',
        rejected:    'status--red',
        pending:     'status--amber',
        in_workflow: 'status--blue',
        in_progress: 'status--blue',
        calculated:  'status--gray',
        draft:       'status--gray',
      };
      return map[status] ?? 'status--gray';
    },
  },
};
</script>


<style scoped>
/* -- Page ---------------------------------------------------------------- */
.back-row    { margin-bottom:10px; }
.btn-back    { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; padding:0; }
.btn-back:hover { color:#004AAD; }
.page-header { margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; display:flex; align-items:center; gap:6px; }
.breadcrumb-link { color:#7a90b0; text-decoration:none; }
.breadcrumb-link:hover { color:#004AAD; }
.breadcrumb-sep { color:#c5d0e0; }
.me-1 { margin-right:4px; }
.ms-1 { margin-left:4px; }
.mt-3 { margin-top:12px; }
.opt  { font-size:11px; color:#94a3b8; font-weight:400; }
.text-muted { color:#7a90b0; font-size:13px; }

/* -- Run header card ----------------------------------------------------- */
.run-header-card   { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:20px 24px; margin-bottom:16px; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:14px; }
.run-header-left   { display:flex; align-items:center; gap:14px; }
.run-header-icon   { width:48px; height:48px; border-radius:12px; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
.run-header-title-row { display:flex; align-items:center; gap:10px; margin-bottom:3px; }
.run-period        { font-size:18px; font-weight:800; color:#1a2b45; margin:0; }
.run-date          { font-size:12.5px; color:#7a90b0; margin:0; }
.run-header-actions { display:flex; align-items:center; gap:10px; }

/* -- Workflow states ----------------------------------------------------- */
.workflow-pill          { display:inline-flex; align-items:center; gap:7px; font-size:13px; font-weight:600; padding:8px 16px; border-radius:10px; }
.workflow-pill--green   { background:rgba(0,74,173,.06); color:#004AAD; border:1px solid rgba(0,74,173,.2); }
.workflow-pill--amber   { background:#fefce8; color:#ca8a04; border:1px solid #fde68a; }
.workflow-pill--teal    { background:#f0fdfa; color:#0d9488; border:1px solid #99f6e4; }
.workflow-pill--purple  { background:#faf5ff; color:#7c3aed; border:1px solid #ddd6fe; }
.btn-initiate          { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:700; padding:9px 18px; border-radius:9px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-initiate:hover    { background:#003b8e; }
.btn-initiate--teal    { background:#0d9488; }
.btn-initiate--teal:hover { background:#0f766e; }
.btn-initiate--purple  { background:#7c3aed; }
.btn-initiate--purple:hover { background:#6d28d9; }
.btn-initiate:disabled { opacity:.6; cursor:not-allowed; }

/* -- Status badge -------------------------------------------------------- */
.status-badge    { display:inline-block; font-size:11.5px; font-weight:700; border-radius:20px; padding:3px 10px; text-transform:capitalize; }
.status--green   { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
.status--red     { background:#fff0f0; color:#e03535; border:1px solid #fecaca; }
.status--amber   { background:#fefce8; color:#ca8a04; border:1px solid #fde68a; }
.status--blue    { background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; }
.status--gray    { background:#f4f7fb; color:#64748b; border:1px solid #e4eaf2; }
.status--teal    { background:#f0fdfa; color:#0d9488; border:1px solid #99f6e4; }
.status--purple  { background:#faf5ff; color:#7c3aed; border:1px solid #ddd6fe; }

/* -- Summary cards ------------------------------------------------------- */
.summary-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:20px; }
.summary-card   { background:#fff; border:1px solid #e4eaf2; border-radius:10px; padding:16px 20px; }
.summary-card--red   { border-color:#fecaca; background:#fff5f5; }
.summary-card--green { border-color:#bbf7d0; background:#f0fdf4; }
.summary-label  { font-size:12.5px; color:#7a90b0; margin:0 0 6px; }
.summary-val    { font-size:22px; font-weight:800; color:#1a2b45; margin:0; }
.summary-val--red   { color:#e03535; }
.summary-val--green { color:#16a34a; }

/* -- Data card ----------------------------------------------------------- */
.data-card       { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-card-hd    { padding:18px 24px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; gap:12px; }
.data-card-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }

/* -- Table --------------------------------------------------------------- */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:11px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; text-align:left; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }

/* -- Employee cell ------------------------------------------------------- */
.emp-cell   { display:flex; align-items:center; gap:10px; }
.emp-avatar { width:34px; height:34px; border-radius:50%; background:#004AAD; color:#fff; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.emp-name   { font-size:13px; font-weight:600; color:#1a2b45; margin:0; }
.emp-pos    { font-size:11.5px; color:#7a90b0; margin:0; }

/* -- Money colours ------------------------------------------------------- */
.text-earning { color:#16a34a; font-weight:500; }
.text-red     { color:#e03535; font-weight:500; }
.text-orange  { color:#ea580c; font-weight:500; }
.text-net     { color:#004AAD; font-weight:700; }

/* -- Payslip button ------------------------------------------------------ */
.btn-payslip { display:inline-flex; align-items:center; gap:5px; background:rgba(0,74,173,.06); border:1px solid rgba(0,74,173,.2); color:#004AAD; font-size:12px; font-weight:600; padding:5px 12px; border-radius:7px; cursor:pointer; transition:all .15s; font-family:inherit; }
.btn-payslip:hover { background:#004AAD; color:#fff; }

/* -- State block --------------------------------------------------------- */
.state-block { display:flex; flex-direction:column; align-items:center; padding:80px 24px; text-align:center; color:#7a90b0; }

/* -- Modals -------------------------------------------------------------- */
.modal-overlay      { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
.modal-overlay.open { opacity:1; pointer-events:all; }
.modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.5); backdrop-filter:blur(2px); }
.modal-box          { background:#fff; border-radius:14px; width:100%; max-width:480px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.2); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; display:flex; flex-direction:column; max-height:90vh; }
.modal-overlay.open .modal-box { transform:translateY(0); }
.modal-close-btn    { width:30px; height:30px; border-radius:7px; border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.12); cursor:pointer; display:flex; align-items:center; justify-content:center; color:rgba(255,255,255,.8); font-size:13px; transition:background .15s; }
.modal-close-btn:hover { background:rgba(255,255,255,.2); }
.modal-bd           { padding:20px 24px; display:flex; flex-direction:column; gap:14px; overflow-y:auto; flex:1; }
.modal-ft           { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; flex-shrink:0; }

/* -- Workflow modal header ----------------------------------------------- */
.workflow-modal-hd       { background:#004AAD; padding:20px 24px; display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-shrink:0; }
.workflow-modal-hd-inner { display:flex; align-items:center; gap:12px; }
.workflow-hd-icon        { width:40px; height:40px; border-radius:10px; background:rgba(255,255,255,.15); color:#fff; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.workflow-modal-title    { font-size:15px; font-weight:700; color:#fff; margin:0 0 2px; }
.workflow-modal-sub      { font-size:12px; color:rgba(255,255,255,.65); margin:0; }

/* -- Info banner --------------------------------------------------------- */
.info-banner      { display:flex; gap:10px; background:#eff6ff; border:1px solid #bfdbfe; border-radius:8px; padding:12px 14px; }
.info-banner-icon { color:#2563eb; font-size:14px; flex-shrink:0; margin-top:1px; }
.info-banner p    { font-size:12.5px; color:#1e40af; line-height:1.6; margin:0; }

/* -- Workflow summary tiles ---------------------------------------------- */
.workflow-summary-grid  { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.workflow-tile          { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:12px; text-align:center; }
.workflow-tile--green   { background:rgba(0,74,173,.04); border-color:rgba(0,74,173,.2); }
.workflow-tile-label    { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 5px; }
.workflow-tile-val      { font-size:16px; font-weight:800; color:#1a2b45; margin:0; }
.workflow-tile-val--green { color:#004AAD; }

/* -- Fields ------------------------------------------------------------- */
.field-group  { display:flex; flex-direction:column; gap:5px; }
.field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
.field-input  { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:9px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; resize:vertical; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-input:disabled { background:#f4f7fb; }

/* -- Buttons ------------------------------------------------------------- */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
.btn-primary-action--teal { background:#0d9488; }
.btn-primary-action--teal:hover:not(:disabled) { background:#0f766e; }
.btn-primary-action--purple { background:#7c3aed; }
.btn-primary-action--purple:hover:not(:disabled) { background:#6d28d9; }
.modal-box--sm { max-width:420px; }
.workflow-hd-icon--teal   { background:rgba(13,148,136,.2); }
.workflow-hd-icon--purple { background:rgba(124,58,237,.2); }
.info-banner--teal { background:#f0fdfa; border-color:#99f6e4; }
.info-banner--teal .info-banner-icon { color:#0d9488; }
.info-banner--teal p { color:#134e4a; }
.info-banner--purple { background:#faf5ff; border-color:#ddd6fe; }
.info-banner--purple .info-banner-icon { color:#7c3aed; }
.info-banner--purple p { color:#4c1d95; }
.btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-ghost:hover:not(:disabled) { background:#e4eaf2; }
.btn-ghost:disabled { opacity:.45; cursor:not-allowed; }

/* -- Payslip modal ------------------------------------------------------- */
.payslip-modal-box { background:#fff; border-radius:16px; width:100%; max-width:520px; margin:16px; box-shadow:0 24px 64px rgba(0,0,0,.22); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; max-height:92vh; display:flex; flex-direction:column; }
.modal-overlay.open .payslip-modal-box { transform:translateY(0); }

.payslip-toolbar       { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #f0f4f8; background:#fff; flex-shrink:0; }
.payslip-toolbar-left  { display:flex; align-items:center; gap:8px; }
.toolbar-icon          { color:#004AAD; font-size:14px; }
.toolbar-title         { font-size:13px; font-weight:600; color:#1a2b45; }
.payslip-toolbar-right { display:flex; align-items:center; gap:6px; }
.btn-download          { display:inline-flex; align-items:center; gap:5px; background:#004AAD; color:#fff; font-size:12px; font-weight:600; padding:5px 12px; border-radius:7px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-download:hover:not(:disabled) { background:#003b8e; }
.btn-download:disabled { opacity:.6; cursor:not-allowed; }
.btn-toolbar-ghost { width:30px; height:30px; border-radius:7px; border:none; background:transparent; color:#7a90b0; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:14px; }
.btn-toolbar-ghost:hover { background:#f4f7fb; }

/* -- Payslip content ----------------------------------------------------- */
.payslip-body { flex:1; overflow-y:auto; }

.ps-header           { background:#004AAD; padding:22px 24px 20px; }
.ps-header-label     { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.16em; color:rgba(255,255,255,.6); margin-bottom:14px; display:flex; align-items:center; gap:6px; }
.ps-employee-row     { display:flex; align-items:flex-start; gap:14px; margin-bottom:18px; }
.ps-avatar           { width:52px; height:52px; border-radius:14px; background:rgba(255,255,255,.18); border:2px solid rgba(255,255,255,.3); color:#fff; font-size:18px; font-weight:800; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.ps-emp-info         { flex:1; min-width:0; }
.ps-name             { font-size:17px; font-weight:800; color:#fff; margin:0 0 4px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ps-meta             { font-size:11.5px; color:rgba(255,255,255,.65); margin:0 0 8px; }
.ps-chips            { display:flex; gap:6px; flex-wrap:wrap; }
.ps-chip             { background:rgba(255,255,255,.18); border-radius:5px; padding:2px 10px; font-size:9.5px; font-weight:600; color:rgba(255,255,255,.9); }
.ps-totals-row       { display:grid; grid-template-columns:repeat(3,1fr); border:1px solid rgba(255,255,255,.2); border-radius:10px; overflow:hidden; }
.ps-total-cell       { background:rgba(255,255,255,.1); padding:11px 14px; text-align:center; border-right:1px solid rgba(255,255,255,.15); }
.ps-total-cell:last-child { border-right:none; }
.ps-total-cell--bright { background:rgba(255,255,255,.2); }
.ps-total-label      { font-size:8.5px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:rgba(255,255,255,.55); margin:0 0 4px; }
.ps-total-val        { font-size:13.5px; font-weight:700; color:#fff; margin:0; }
.ps-total-val--red   { color:#fca5a5; }

.ps-detail           { padding:18px 22px; }
.ps-divider          { display:flex; align-items:center; gap:10px; padding:8px 0; font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:#94a3b8; }
.ps-divider::before, .ps-divider::after { content:''; flex:1; height:1px; background:#f0f4f8; }
.ps-line-items       { border:1px solid #e4eaf2; border-radius:8px; overflow:hidden; margin-bottom:6px; }
.ps-line             { display:flex; justify-content:space-between; padding:8px 14px; font-size:12.5px; border-bottom:1px solid #f8fafc; }
.ps-line:last-child  { border-bottom:none; }
.ps-line span:first-child { color:#6b7280; }
.ps-earning          { color:#059669; font-weight:600; }
.ps-orange           { color:#ea580c; font-weight:600; }
.ps-red              { color:#dc2626; font-weight:600; }
.ps-subtotal         { display:flex; justify-content:space-between; background:#f3f4f6; border-radius:7px; padding:8px 14px; font-size:12px; font-weight:700; color:#374151; margin-bottom:12px; }
.ps-subtotal--green  { background:#ecfdf5; color:#059669; }
.ps-subtotal--orange { background:#fff7ed; color:#ea580c; }
.ps-subtotal--red    { background:#fef2f2; color:#dc2626; }
.ps-summary-row      { display:flex; align-items:center; gap:12px; border:1px solid #e4eaf2; border-radius:10px; padding:12px 16px; margin-bottom:12px; }
.ps-summary-icon     { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.ps-summary-icon--blue   { background:#eff6ff; color:#2563eb; }
.ps-summary-icon--purple { background:#f5f3ff; color:#7c3aed; }
.ps-summary-lbl      { flex:1; font-size:13px; font-weight:600; color:#374151; }
.ps-summary-val      { font-size:15px; font-weight:800; color:#1a2b45; }
.ps-net-banner       { background:#004AAD; border-radius:12px; padding:18px 22px; display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.ps-net-label        { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.18em; color:rgba(255,255,255,.6); margin:0 0 5px; }
.ps-net-val          { font-size:28px; font-weight:800; color:#fff; margin:0; line-height:1; }
.ps-net-icon         { width:48px; height:48px; border-radius:12px; background:rgba(255,255,255,.12); display:flex; align-items:center; justify-content:center; font-size:20px; color:rgba(255,255,255,.8); }
.ps-footer-note      { text-align:center; font-size:11px; color:#94a3b8; padding-bottom:8px; }

/* -- Export button ------------------------------------------------------- */
.btn-export-trigger { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#004AAD; border:1.5px solid rgba(0,74,173,.3); font-size:13px; font-weight:600; padding:7px 14px; border-radius:8px; cursor:pointer; transition:all .15s; font-family:inherit; }
.btn-export-trigger:hover { background:#004AAD; color:#fff; border-color:#004AAD; }

/* -- Export modal -------------------------------------------------------- */
.modal-box--xl { max-width:820px; }
.export-modal-bd { gap:16px; }

.export-toolbar { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px; }
.export-hint    { font-size:13px; color:#4a5e78; }
.export-toolbar-actions { display:flex; gap:6px; }

.btn-xs-outline { font-size:11.5px; font-weight:600; padding:4px 10px; border-radius:6px; border:1px solid #dde5f0; background:#fff; color:#1a2b45; cursor:pointer; transition:background .12s; font-family:inherit; }
.btn-xs-outline:hover { background:#f4f7fb; }
.btn-xs-link { font-size:11px; font-weight:600; color:#004AAD; background:none; border:none; cursor:pointer; padding:0; font-family:inherit; text-decoration:underline; text-underline-offset:2px; }
.btn-xs-link:hover { color:#003b8e; }

.export-groups  { display:flex; flex-direction:column; gap:14px; }
.export-group   { border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; }
.export-group-hd { display:flex; align-items:center; justify-content:space-between; padding:9px 14px; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.export-group-label { font-size:12px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#4a5e78; }
.export-cols    { display:flex; flex-wrap:wrap; gap:4px; padding:10px 14px; }
.export-col-item { display:inline-flex; align-items:center; gap:6px; padding:5px 10px; border-radius:6px; border:1px solid #e4eaf2; background:#fff; cursor:pointer; font-size:12.5px; color:#1a2b45; transition:all .12s; user-select:none; }
.export-col-item:hover { background:#eff6ff; border-color:#bfdbfe; }
.export-col-item input[type="checkbox"] { width:13px; height:13px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.export-col-item:has(input:checked) { background:#eff6ff; border-color:#004AAD; color:#004AAD; font-weight:600; }

.btn-export-csv { display:inline-flex; align-items:center; gap:6px; background:#16a34a; color:#fff; font-size:13px; font-weight:600; padding:9px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-export-csv:hover:not(:disabled) { background:#15803d; }
.btn-export-csv:disabled { opacity:.5; cursor:not-allowed; }
.btn-export-pdf { display:inline-flex; align-items:center; gap:6px; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:9px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
.btn-export-pdf:hover:not(:disabled) { background:#b91c1c; }
.btn-export-pdf:disabled { opacity:.5; cursor:not-allowed; }

/* -- Payment method cards ------------------------------------------------ */
.pay-method-cards { display:flex; flex-direction:column; gap:10px; }
.pay-method-card  { display:flex; align-items:center; gap:12px; background:#fff; border:2px solid #e4eaf2; border-radius:11px; padding:14px 16px; cursor:pointer; text-align:left; font-family:inherit; transition:border-color .15s, background .15s; width:100%; }
.pay-method-card:hover:not(:disabled) { border-color:#0d9488; background:#f0fdfa; }
.pay-method-card.selected { border-color:#0d9488; background:#f0fdfa; }
.pay-method-card:disabled { opacity:.55; cursor:not-allowed; }
.pay-method-card-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
.pay-method-card-icon--teal  { background:#ccfbf1; color:#0d9488; }
.pay-method-card-icon--green { background:#dcfce7; color:#16a34a; }
.pay-method-card-body { flex:1; min-width:0; }
.pay-method-card-title { font-size:13.5px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.pay-method-card-desc  { font-size:12px; color:#7a90b0; margin:0; line-height:1.4; }
.pay-method-check { color:#0d9488; font-size:16px; flex-shrink:0; }

/* -- Responsive ---------------------------------------------------------- */
@media (max-width:900px)  { .summary-grid { grid-template-columns:1fr 1fr; } }
@media (max-width:640px)  {
  .summary-grid { grid-template-columns:1fr; }
  .run-header-card { flex-direction:column; align-items:flex-start; }
  .workflow-summary-grid { grid-template-columns:1fr; }
}
</style>